"use client";

import {
  listingsService,
  type Listing,
  type ListingsFilters,
} from "@/services/api";
import { useCallback, useState } from "react";

interface UseListingsOptions {
  limit?: number;
}

interface UseListingsReturn {
  listings: Listing[];
  brands: string[];
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
  fetchListings: (filters?: Partial<ListingsFilters>, page?: number) => Promise<void>;
  setCurrentPage: (page: number) => void;
}

export function useListings({ limit = 50 }: UseListingsOptions = {}): UseListingsReturn {
  const [listings, setListings] = useState<Listing[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchListings = useCallback(async (filters: Partial<ListingsFilters> = {}, page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await listingsService.getListings({ page, limit, ...filters });
      setListings(data.listings);
      setBrands(data.brands);
      setTotalPages(data.totalPages);
      setCurrentPage(data.page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch listings";
      setError(errorMessage);
      setListings([]);
      setBrands([]);
      setTotalPages(1);
      console.error("Error fetching listings:", err);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  return {
    listings,
    brands,
    totalPages,
    currentPage,
    isLoading,
    error,
    fetchListings,
    setCurrentPage,
  };
}
